import {
    fetchBaseQuery,
    type BaseQueryApi,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
    type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import { accessToken } from '../../configuration/tokenHandling/accessToken';

type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

export interface JsonObject {
    [key: string]: JsonValue;
}

export type ApiServiceName = 'assetAdmin' | 'tagService' | 'marketplace' | 'latamMarketplace';

export interface ApiError {
    status: number;
    detail: string | null;
    code: string | null;
}

type QueryParamValue = string | number | boolean | null | undefined;

export interface ServiceQueryArgs {
    service?: ApiServiceName;
    path?: string;
    absoluteUrl?: string;
    method?: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE';
    body?: JsonObject | JsonValue[];
    headers?: Record<string, string>;
    params?: Record<string, QueryParamValue>;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: '',
    responseHandler: async response => {
        const text = await response.text();
        if (!text) {
            return null;
        }

        try {
            return JSON.parse(text) as JsonValue;
        } catch {
            return text;
        }
    },
});

const backendUrlByService: Record<ApiServiceName, string | undefined> = {
    assetAdmin: config.backend.assetAdmin,
    tagService: config.backend.tagService,
    marketplace: config.backend.marketplace,
    latamMarketplace: config.backend.latamMarketplace,
};

const ensureSlashPrefixedPath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

const sanitizeParams = (params?: Record<string, QueryParamValue>) => {
    if (!params) {
        return undefined;
    }

    return Object.fromEntries(
        Object.entries(params).filter(
            (entry): entry is [string, string | number | boolean] => entry[1] !== undefined && entry[1] !== null
        )
    );
};

const resolveUrl = (args: ServiceQueryArgs) => {
    if (args.absoluteUrl) {
        return args.absoluteUrl;
    }

    if (!args.service) {
        throw new Error('service is required when absoluteUrl is not provided');
    }

    if (!args.path) {
        throw new Error(`path is required for service "${args.service}"`);
    }

    const serviceBaseUrl = backendUrlByService[args.service];
    if (!serviceBaseUrl) {
        throw new Error(`Base URL is missing for service "${args.service}"`);
    }

    return `${serviceBaseUrl}${ensureSlashPrefixedPath(args.path)}`;
};

const buildHeaders = (args: ServiceQueryArgs) => {
    const headers = new Headers(args.headers);
    const bearerToken = accessToken.getAccessToken();

    if (bearerToken) {
        headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    if (args.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return headers;
};

const buildFetchArgs = (args: ServiceQueryArgs): FetchArgs => ({
    url: resolveUrl(args),
    method: args.method ?? 'GET',
    body: args.body,
    headers: buildHeaders(args),
    params: sanitizeParams(args.params),
});

const getStringProperty = (data: FetchBaseQueryError['data'], property: 'detail' | 'code') => {
    if (!data || typeof data !== 'object' || Array.isArray(data) || !(property in data)) {
        return null;
    }

    const value = data[property];
    return typeof value === 'string' ? value : null;
};

const getErrorCode = (data: FetchBaseQueryError['data'], detail: string | null) => {
    const explicitCode = getStringProperty(data, 'code');
    if (explicitCode) {
        return explicitCode;
    }

    const match = detail ? /^\[([^\]]+)]/.exec(detail) : null;
    return match?.[1] ?? null;
};

const toApiError = (error: FetchBaseQueryError): ApiError => {
    if (typeof error.status === 'number') {
        const detail = getStringProperty(error.data, 'detail');
        return {
            status: error.status,
            detail,
            code: getErrorCode(error.data, detail),
        };
    }

    switch (error.status) {
        case 'PARSING_ERROR':
            return { status: error.originalStatus, detail: error.error, code: null };
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
        case 'CUSTOM_ERROR':
            return { status: 0, detail: error.error, code: null };
    }
};

export const isApiError = (error: object | null | undefined): error is ApiError =>
    Boolean(error && 'status' in error && 'detail' in error && 'code' in error);

export const serviceBaseQuery: BaseQueryFn<ServiceQueryArgs, JsonValue | null, ApiError, {}, FetchBaseQueryMeta> = async (
    args: ServiceQueryArgs,
    api: BaseQueryApi,
    extraOptions: {}
) => {
    const result = await rawBaseQuery(buildFetchArgs(args), api, extraOptions);

    if (result.error) {
        return { error: toApiError(result.error), meta: result.meta };
    }

    return { data: result.data as JsonValue | null, meta: result.meta };
};
